import React, { useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, Clock3, Loader2, Send } from "lucide-react";
import { toast } from "react-toastify";

import {
  acceptMadeToMeasureCustomerUpdateQuote,
  getMadeToMeasureCustomerUpdate,
  resetMadeToMeasureAcceptCustomerUpdateQuote,
  resetMadeToMeasureCustomerResponse,
  resetMadeToMeasureCustomerUpdate,
  respondToMadeToMeasureCustomerUpdateQuote,
} from "../../actions/madeToMeasureActions";
import { getPaymentConfig } from "../../actions/paymentActions";
import Meta from "../../components/Meta";
import {
  selectMadeToMeasureAcceptCustomerUpdate,
  selectMadeToMeasureCustomerResponse,
  selectMadeToMeasureCustomerUpdate,
} from "../../selectors/madeToMeasureSelectors";
import { selectPaymentConfig } from "../../selectors/paymentSelectors";
import { getSafeUserMessage } from "../../utils/errorMessages";

import "./MadeToMeasureScreen.css";

const FALLBACK_IMAGE = "https://placehold.co/640x760?text=Academy+Costumes+London";

const MEASUREMENT_FIELDS = [
  ["waist", "Waist"],
  ["hip", "Hip"],
  ["bust", "Bust"],
  ["length", "Length"],
  ["shoulder", "Shoulder"],
  ["sleeve", "Sleeve"],
];

const STATUS_COPY = {
  new: {
    title: "Enquiry received",
    text: "Your selections are with our team and waiting for review.",
  },
  reviewing: {
    title: "Under review",
    text: "We are checking the design, textile, trims, and measurements.",
  },
  needs_details: {
    title: "More details needed",
    text: "Our team needs a little more information before preparing the final quote.",
  },
  quoted: {
    title: "Quote prepared",
    text: "Your final quote is being prepared for publication.",
  },
  quote_sent: {
    title: "Final quote ready",
    text: "Review the final quote below, then accept or decline it.",
  },
  accepted: {
    title: "Quote accepted",
    text: "Your quote has been accepted. You can now continue to secure payment.",
  },
  payment_pending: {
    title: "Payment pending",
    text: "Payment has started. If you did not complete it, you can try again.",
  },
  paid: {
    title: "Payment received",
    text: "Your paid made to measure order is ready for production review.",
  },
  in_production: {
    title: "In production",
    text: "Your made to measure order is now in production.",
  },
  completed: {
    title: "Completed",
    text: "This made to measure request has been completed.",
  },
  rejected: {
    title: "Quote declined",
    text: "You declined this final quote. Please contact us if you need help.",
  },
};

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function formatDate(value = "") {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return "";
  }
}

function getImage(item = {}) {
  return safeText(item.image || item.defaultImage || item.images?.[0]?.url);
}

function getName(item = {}) {
  return item?.name || item?.productName || "Not selected";
}

function getMaterial(item = {}) {
  return (
    safeText(item.originalMaterial) ||
    safeText(item.fabricType) ||
    safeText(item.materialType) ||
    safeText(item.material) ||
    "Material not listed"
  );
}

function getShortReference(enquiry = {}) {
  const id = safeText(enquiry.id || enquiry._id);
  return id ? `MTM-${id.slice(-6).toUpperCase()}` : "";
}

function getMeasurementRows(measurements = {}) {
  const unit = safeText(measurements.unit) || "cm";

  return MEASUREMENT_FIELDS.map(([key, label]) => {
    const value = safeText(measurements[key]);
    return value ? `${label}: ${value} ${unit}` : "";
  }).filter(Boolean);
}

function paymentUrl(payload = {}) {
  return (
    payload.checkoutUrl ||
    payload.approvalUrl ||
    payload.approveUrl ||
    payload.payerActionUrl ||
    payload.nextAction?.url ||
    ""
  );
}

function SummaryRow({ item, label, meta = "", price = "" }) {
  if (!item) return null;

  return (
    <div className="mtm-summary__item">
      <img
        src={getImage(item) || FALLBACK_IMAGE}
        alt={item.name || label}
        onError={(event) => {
          event.currentTarget.src = FALLBACK_IMAGE;
        }}
      />
      <div>
        <span>{label}</span>
        <strong>{getName(item)}</strong>
        {meta ? <small>{meta}</small> : null}
      </div>
      {price ? <em>{price}</em> : null}
    </div>
  );
}

function TimelineItem({ complete, title, text }) {
  return (
    <li className={complete ? "is-complete" : ""}>
      {complete ? <CheckCircle2 size={16} /> : <Clock3 size={16} />}
      <span>
        <strong>{title}</strong>
        <small>{text}</small>
      </span>
    </li>
  );
}

export default function MadeToMeasureEnquiryStatusScreen() {
  const { token = "" } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const updateState = useSelector(selectMadeToMeasureCustomerUpdate);
  const responseState = useSelector(selectMadeToMeasureCustomerResponse);
  const acceptState = useSelector(selectMadeToMeasureAcceptCustomerUpdate);
  const paymentConfig = useSelector(selectPaymentConfig);

  const enquiry = updateState.enquiry;
  const statusCopy = STATUS_COPY[enquiry?.status] || STATUS_COPY.new;
  const paymentReturn = safeText(searchParams.get("payment"));
  const stripeEnabled = Boolean(paymentConfig.stripeEnabled);
  const paypalEnabled = Boolean(paymentConfig.paypalEnabled);
  const sumupEnabled = Boolean(paymentConfig.sumupEnabled);
  const canRespond = Boolean(enquiry?.canRespondToQuote || enquiry?.canAcceptQuote);
  const canPay = Boolean(enquiry?.canPayQuote);

  useEffect(() => {
    dispatch(getMadeToMeasureCustomerUpdate(token)).catch((error) => {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load this enquiry update right now. Please try again later.",
        ),
      );
    });

    dispatch(getPaymentConfig()).catch(() => {});

    return () => {
      dispatch(resetMadeToMeasureCustomerUpdate());
      dispatch(resetMadeToMeasureCustomerResponse());
      dispatch(resetMadeToMeasureAcceptCustomerUpdateQuote());
    };
  }, [dispatch, token]);

  const handleQuoteResponse = async (decision) => {
    if (decision === "decline") {
      const confirmed = window.confirm(
        "Decline this final quote? You will need to contact us if you change your mind.",
      );

      if (!confirmed) return;
    }

    try {
      const result = await dispatch(
        respondToMadeToMeasureCustomerUpdateQuote(token, { decision }),
      );

      toast.success(result?.message || "Your response has been saved.");
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not save your quote response right now. Please try again later.",
        ),
      );
    }
  };

  const handlePayment = async (provider) => {
    try {
      const result = await dispatch(
        acceptMadeToMeasureCustomerUpdateQuote(token, { provider }),
      );
      const url = paymentUrl(result);

      if (url) {
        window.location.assign(url);
        return;
      }

      toast.info("Payment was prepared, but we could not open the payment page.");
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not continue to payment right now. Please try again later.",
        ),
      );
    }
  };

  return (
    <main className="mtm-page">
      <Meta
        title="Made To Measure Updates | Academy Costumes London"
        description="View your made to measure enquiry updates and final quote."
      />

      <header className="mtm-hero">
        <p className="mtm-eyebrow">Customer updates</p>
        <h1>Made To Measure Updates</h1>
        <p>
          Follow your enquiry, review the final quote when it is ready, and pay
          securely before production begins.
        </p>
      </header>

      {updateState.loading ? (
        <section className="mtm-state">
          <Loader2 className="mtm-spin" />
          <p>Loading your enquiry update...</p>
        </section>
      ) : updateState.error ? (
        <section className="mtm-state">
          <p>{updateState.error}</p>
          <Link to="/contact" className="mtm-nav-button mtm-nav-button--primary">
            Contact us
          </Link>
        </section>
      ) : enquiry ? (
        <section className="mtm-quote">
          <div className="mtm-step-panel">
            {paymentReturn === "success" ? (
              <div className="mtm-success">
                <CheckCircle2 size={24} />
                <div>
                  <strong>Thank you.</strong>
                  <p>
                    If your payment has completed, this page will update once it
                    has been confirmed.
                  </p>
                </div>
              </div>
            ) : null}

            {paymentReturn === "cancelled" ? (
              <div className="mtm-warning mtm-warning--full">
                <span aria-hidden="true">!</span>
                <span>Payment was cancelled. You can still accept this quote.</span>
              </div>
            ) : null}

            <div className="mtm-status-card">
              <span>{enquiry.status}</span>
              <h2>{statusCopy.title}</h2>
              <p>{statusCopy.text}</p>
              {getShortReference(enquiry) ? (
                <small>Reference {getShortReference(enquiry)}</small>
              ) : null}
              {enquiry.updatedAt ? (
                <small>Last updated {formatDate(enquiry.updatedAt)}</small>
              ) : null}
            </div>

            <div className="mtm-summary mtm-summary--quote">
              <SummaryRow
                item={enquiry.selectedProduct}
                label="Design"
                meta={`Original material: ${getMaterial(enquiry.selectedProduct)}`}
                price={formatMoney(enquiry.estimatedBasePrice)}
              />
              <SummaryRow
                item={enquiry.selectedTextile}
                label="Fabric"
                meta={getMaterial(enquiry.selectedTextile)}
              />

              <div className="mtm-summary__group">
                <h3>Selected trims</h3>
                {enquiry.selectedHaberdashery?.length ? (
                  enquiry.selectedHaberdashery.map((item) => (
                    <SummaryRow key={item.id || item._id} item={item} label="Trim" />
                  ))
                ) : (
                  <p>No trims selected.</p>
                )}
              </div>

              <div className="mtm-summary__group">
                <h3>Your request</h3>
                {enquiry.notes ? <p>{enquiry.notes}</p> : <p>No extra notes added.</p>}
                {getMeasurementRows(enquiry.measurements).length ? (
                  <ul className="mtm-summary__measurements mtm-summary__measurements--plain">
                    {getMeasurementRows(enquiry.measurements).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p>Measurements can be confirmed with our team.</p>
                )}
              </div>

              <div className="mtm-summary__group">
                <h3>Updates</h3>
                <ul className="mtm-status-timeline">
                  <TimelineItem
                    complete
                    title="Enquiry received"
                    text={formatDate(enquiry.createdAt) || "Submitted"}
                  />
                  <TimelineItem
                    complete={enquiry.status !== "new"}
                    title="Admin review"
                    text="Design, fabric, trims, and measurements checked"
                  />
                  <TimelineItem
                    complete={Boolean(enquiry.quoteSentAt)}
                    title="Final quote"
                    text={
                      enquiry.quoteSentAt
                        ? `Published ${formatDate(enquiry.quoteSentAt)}`
                        : "Not published yet"
                    }
                  />
                  <TimelineItem
                    complete={Boolean(
                      enquiry.quoteAcceptedAt || enquiry.quoteDeclinedAt,
                    )}
                    title="Your response"
                    text={
                      enquiry.quoteDeclinedAt
                        ? `Declined ${formatDate(enquiry.quoteDeclinedAt)}`
                        : enquiry.quoteAcceptedAt
                          ? `Accepted ${formatDate(enquiry.quoteAcceptedAt)}`
                          : "Waiting for your decision"
                    }
                  />
                  <TimelineItem
                    complete={Boolean(enquiry.quotePaidAt)}
                    title="Payment"
                    text={
                      enquiry.quotePaidAt
                        ? `Received ${formatDate(enquiry.quotePaidAt)}`
                        : "Required before production"
                    }
                  />
                  <TimelineItem
                    complete={["in_production", "completed"].includes(
                      enquiry.status,
                    )}
                    title="Production"
                    text={
                      enquiry.status === "completed"
                        ? "Completed"
                        : enquiry.status === "in_production"
                          ? "In production"
                          : "Starts after payment and final confirmation"
                    }
                  />
                </ul>
              </div>

              {enquiry.quoteMessage ? (
                <div className="mtm-summary__group">
                  <h3>Message from our team</h3>
                  <p>{enquiry.quoteMessage}</p>
                </div>
              ) : null}

              {enquiry.customerResponseMessage ? (
                <div className="mtm-summary__group">
                  <h3>Your response note</h3>
                  <p>{enquiry.customerResponseMessage}</p>
                </div>
              ) : null}

              <div className="mtm-summary__estimate">
                <div>
                  <span>Estimated base</span>
                  <strong>{formatMoney(enquiry.estimatedBasePrice)}</strong>
                </div>
                <div>
                  <span>Estimated extra</span>
                  <strong>{formatMoney(enquiry.estimatedExtraCost)}</strong>
                </div>
                <div>
                  <span>Final quote</span>
                  <strong>
                    {enquiry.finalQuotePrice
                      ? formatMoney(enquiry.finalQuotePrice)
                      : "Pending review"}
                  </strong>
                </div>
              </div>
            </div>

            {enquiry.quoteExpired ? (
              <div className="mtm-warning mtm-warning--full">
                <span aria-hidden="true">!</span>
                <span>
                  This final quote has expired. Please contact us for a refreshed
                  quote.
                </span>
              </div>
            ) : null}

            {enquiry.status === "paid" || enquiry.status === "completed" ? (
              <div className="mtm-success mtm-success--stacked">
                <CheckCircle2 size={24} />
                <div>
                  <strong>Payment received.</strong>
                  <p>Our team will review production details before making begins.</p>
                </div>
              </div>
            ) : null}

            {enquiry.status === "rejected" ? (
              <div className="mtm-warning mtm-warning--full">
                <span aria-hidden="true">!</span>
                <span>
                  You declined this quote. Contact us if you would like to reopen
                  or adjust the enquiry.
                </span>
              </div>
            ) : null}

            {canRespond ? (
              <div className="mtm-submit-row">
                <p>
                  Accept this final quote to unlock secure payment, or decline it
                  if you do not want to proceed.
                </p>

                <div className="mtm-payment-actions">
                  <button
                    type="button"
                    className="mtm-nav-button mtm-nav-button--primary"
                    disabled={responseState.loading}
                    onClick={() => handleQuoteResponse("accept")}
                  >
                    {responseState.loading ? (
                      <Loader2 size={16} className="mtm-spin" />
                    ) : (
                      <CheckCircle2 size={16} />
                    )}
                    Accept quote
                  </button>

                  <button
                    type="button"
                    className="mtm-nav-button"
                    disabled={responseState.loading}
                    onClick={() => handleQuoteResponse("decline")}
                  >
                    Decline quote
                  </button>
                </div>
              </div>
            ) : null}

            {canPay ? (
              <div className="mtm-submit-row">
                <p>
                  Your quote is accepted. Continue to secure payment when ready.
                  Production starts only after payment and final confirmation.
                </p>

                <div className="mtm-payment-actions">
                  <button
                    type="button"
                    className="mtm-nav-button mtm-nav-button--primary"
                    disabled={!stripeEnabled || acceptState.loading}
                    onClick={() => handlePayment("stripe")}
                  >
                    {acceptState.loading ? (
                      <Loader2 size={16} className="mtm-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    Pay by card
                  </button>

                  <button
                    type="button"
                    className="mtm-nav-button"
                    disabled={!paypalEnabled || acceptState.loading}
                    onClick={() => handlePayment("paypal")}
                  >
                    PayPal
                  </button>

                  {sumupEnabled ? (
                    <button
                      type="button"
                      className="mtm-nav-button"
                      disabled={acceptState.loading}
                      onClick={() => handlePayment("sumup")}
                    >
                      SumUp
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {responseState.error ? (
              <p className="mtm-error">{responseState.error}</p>
            ) : null}

            {!stripeEnabled && !paypalEnabled && !sumupEnabled && canPay ? (
              <p className="mtm-error">
                Online payment is not configured yet. Please contact us to
                accept this quote.
              </p>
            ) : null}

            {acceptState.error ? (
              <p className="mtm-error">{acceptState.error}</p>
            ) : null}
          </div>
        </section>
      ) : null}
    </main>
  );
}
